import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";

// mui
import { Skeleton, Stack, Select, Chip, Card, Typography, Box, Button } from "@mui/material";
import ResultCard from "./ResultCard";
import DownloadIcon from '@mui/icons-material/Download';

// constants
import { QuestionPhase, ResultType } from '../constants';
import { Trans, useTranslation } from "react-i18next";
import { getDescription, getTranslatedText, getResultDataForLegislation} from "../pages/utils_v2/kumuHelper";
import { downloadResultAsPdfOrDocx } from '../pages/utils_v2/downloadUtils';


export default function Results({ legislationResults, setQuestionPhase, legislation }) {
    const { t, i18n } = useTranslation('translation');

    const handleChange = (event) => {
        setQuestionPhase(QuestionPhase.LEGISLATIONS)
    };

    const getCleanLabel = (label) => {
        if (!label) return "";
        const doc = new DOMParser().parseFromString(label, 'text/html');
        return doc.body.textContent || label;
    };

    // Helper function to get result for a legislation
    const getResultForLegislation = (leg) => {
        const legLabel = getCleanLabel(leg.value.attributes.label);
        return legislationResults.find(r => {
            const resultLabel = getCleanLabel(r.name);
            return resultLabel === legLabel;
        });
    };

    // Helper to get legislation data (either from result element or regulation element)
    const getLegislationData = (leg) => {
        // First, try to find a matching "result" element in kumuElements
        const resultElement = getResultDataForLegislation(leg.value.attributes.label, i18n.language);
        
        // If found, return the result element, otherwise return the original legislation
        return resultElement || leg;
    };

    // Categorize ALL legislation based on their results
    const requiredLegislation = legislation.filter(leg => {
        const result = getResultForLegislation(leg);
        return result && result.status === ResultType.REQUIRED;
    });

    const learnLegislation = legislation.filter(leg => {
        const result = getResultForLegislation(leg);
        return result && result.status === ResultType.LEARN;
    });

    const notAnsweredLegislation = legislation.filter(leg => {
        const result = getResultForLegislation(leg);
        return !result || (result.status !== ResultType.REQUIRED && result.status !== ResultType.LEARN);
    });


    return (
        <>
            <Card style={{ 'backgroundColor': '#26374A', 'borderRadius': 10 }}>
                <div style={{ margin: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography sx={{ color: 'white', fontSize: 24 }}>{t('Your results:')}</Typography>
                    <Button 
                        variant="contained"
                        startIcon={<DownloadIcon />}
                        onClick={() => downloadResultAsPdfOrDocx(legislationResults, 'pdf', i18n.language)}
                        sx={{
                            fontSize: '11px',
                            backgroundColor: '#4CAF50',
                            textTransform: 'none',
                            fontWeight: 'bold',
                            boxShadow: 3,
                            '&:hover': {
                                backgroundColor: '#388E3C',
                            }
                        }}
                    >
                        {t('Download Results (PDF)')}
                    </Button>
                </div>

                <div style={{ margin: 10 }}>
                    {legislation.length === 0 ? (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <p style={{ color: 'white' }}>
                                {t("You do not have any results. Please select the \"Back to Guided Search\" button below and answer questions in Step 2 to recieve your results here.")}
                            </p>
                        </div>
                    ) : (
                        <>
                            {/* Required Results Section */}
                            {requiredLegislation.length > 0 && (
                                <>
                                    <Typography 
                                        variant="h5" 
                                        sx={{ 
                                            color: 'white', 
                                            fontWeight: 'bold', 
                                            mt: 2, 
                                            mb: 2,
                                            borderBottom: '2px solid white',
                                            paddingBottom: 1
                                        }}
                                    >
                                        {t('You may have responsibilities')}
                                    </Typography>
                                    {requiredLegislation.sort((a, b) => {
                                        const labelA = getTranslatedText(i18n.language, a.value.attributes.label);
                                        const labelB = getTranslatedText(i18n.language, b.value.attributes.label);
                                        return labelA.localeCompare(labelB);
                                    }).map((leg, idx) => {
                                        const result = getResultForLegislation(leg);
                                        // Use the legislation data from the result if available, otherwise fall back to leg
                                        const legislationData = result?.legislation || getLegislationData(leg);
                                        return (
                                            <ResultCard
                                                key={`required-${idx}`}
                                                title={getTranslatedText(i18n.language, leg.value.attributes.label)}
                                                reason={result?.reason}
                                                explanation={getDescription(legislationData, i18n.language)}
                                                contact={i18n.language === 'fr' ? legislationData.value.attributes['contact fr'] : legislationData.value.attributes['contact']}
                                                guidance={i18n.language === 'fr' ? legislationData.value.attributes['guidance fr'] : legislationData.value.attributes['guidance']}
                                                timelines={i18n.language === 'fr' ? legislationData.value.attributes['timelines fr'] : legislationData.value.attributes['timelines']}
                                                portal={i18n.language === 'fr' ? legislationData.value.attributes['where to apply fr'] : legislationData.value.attributes['where to apply']}
                                                website={i18n.language === 'fr' ? legislationData.value.attributes['program website fr'] : legislationData.value.attributes['program website']}
                                                examples={i18n.language === 'fr' ? legislationData.value.attributes['examples fr'] : legislationData.value.attributes['examples']}
                                                legislationCode={legislationData.value.attributes['legislation']}
                                                status={result?.status}
                                                questionsAnswered={result?.history}
                                                geographical={i18n.language === 'fr' ? legislationData.value.attributes['geographical legislation fr'] : legislationData.value.attributes['geographical legislation']}
                                            />
                                        );
                                    })}
                                </>
                            )}

                            {/* Learn Results Section */}
                            {learnLegislation.length > 0 && (
                                <>
                                    <Typography 
                                        variant="h5" 
                                        sx={{ 
                                            color: 'white', 
                                            fontWeight: 'bold', 
                                            mt: 4, 
                                            mb: 2,
                                            borderBottom: '2px solid white',
                                            paddingBottom: 1
                                        }}
                                    >
                                        {t('You likely do not have responsibilities')}
                                    </Typography>
                                    {learnLegislation.sort((a, b) => {
                                        const labelA = getTranslatedText(i18n.language, a.value.attributes.label);
                                        const labelB = getTranslatedText(i18n.language, b.value.attributes.label);
                                        return labelA.localeCompare(labelB);
                                    }).map((leg, idx) => {
                                        const result = getResultForLegislation(leg);
                                        // Use the legislation data from the result if available, otherwise fall back to leg
                                        const legislationData = result?.legislation || getLegislationData(leg);

                                        return (
                                            <ResultCard
                                                key={`learn-${idx}`}
                                                title={getTranslatedText(i18n.language, leg.value.attributes.label)}
                                                reason={result?.reason}
                                                explanation={getDescription(legislationData, i18n.language)}
                                                contact={i18n.language === 'fr' ? legislationData.value.attributes['contact fr'] : legislationData.value.attributes['contact']}
                                                guidance={i18n.language === 'fr' ? legislationData.value.attributes['guidance fr'] : legislationData.value.attributes['guidance']}
                                                timelines={i18n.language === 'fr' ? legislationData.value.attributes['timelines fr'] : legislationData.value.attributes['timelines']}
                                                portal={i18n.language === 'fr' ? legislationData.value.attributes['where to apply fr'] : legislationData.value.attributes['where to apply']}
                                                website={i18n.language === 'fr' ? legislationData.value.attributes['program website fr'] : legislationData.value.attributes['program website']}
                                                examples={i18n.language === 'fr' ? legislationData.value.attributes['examples fr'] : legislationData.value.attributes['examples']}
                                                legislationCode={legislationData.value.attributes['legislation']}
                                                status={result?.status}
                                                questionsAnswered={result?.history}
                                                geographical={i18n.language === 'fr' ? legislationData.value.attributes['geographical legislation fr'] : legislationData.value.attributes['geographical legislation']}
                                            />
                                        );
                                    })}
                                </>
                            )}

                            {/* Not Answered Results Section */}
                            {notAnsweredLegislation.length > 0 && (
                            <>
                                <Typography 
                                    variant="h5" 
                                    sx={{ 
                                        color: 'white', 
                                        fontWeight: 'bold', 
                                        mt: 4, 
                                        mb: 2,
                                        borderBottom: '2px solid white',
                                        paddingBottom: 1
                                    }}
                                >
                                    {t('Not available, questions not answered')}
                                </Typography>
                                {notAnsweredLegislation.sort((a, b) => {
                                    const labelA = getTranslatedText(i18n.language, a.value.attributes.label);
                                    const labelB = getTranslatedText(i18n.language, b.value.attributes.label);
                                    return labelA.localeCompare(labelB);
                                }).map((leg, idx) => {
                                    // Get the proper legislation data (from result element if available)
                                    const legislationData = getLegislationData(leg);
                                    return (
                                        <ResultCard
                                            key={`not-answered-${idx}`}
                                            title={getTranslatedText(i18n.language, leg.value.attributes.label)}
                                            reason={null}
                                            explanation={null}
                                            contact={i18n.language === 'fr' ? legislationData.value.attributes['contact fr'] : legislationData.value.attributes['contact']}
                                            guidance={i18n.language === 'fr' ? legislationData.value.attributes['guidance fr'] : legislationData.value.attributes['guidance']}
                                            timelines={i18n.language === 'fr' ? legislationData.value.attributes['timelines fr'] : legislationData.value.attributes['timelines']}
                                            portal={i18n.language === 'fr' ? legislationData.value.attributes['where to apply fr'] : legislationData.value.attributes['where to apply']}
                                            website={i18n.language === 'fr' ? legislationData.value.attributes['program website fr'] : legislationData.value.attributes['program website']}
                                            examples={i18n.language === 'fr' ? legislationData.value.attributes['examples fr'] : legislationData.value.attributes['examples']}
                                            legislationCode={legislationData.value.attributes['legislation']}
                                            status="Not answered"
                                            questionsAnswered={[]}
                                            geographical={i18n.language === 'fr' ? legislationData.value.attributes['geographical legislation fr'] : legislationData.value.attributes['geographical legislation']}
                                        />
                                    );
                                })}
                            </>
                        )}
                        </>
                    )}
                </div>
            </Card>
            <br />
            <Box mt={1}>
                <button
                    className="btn btn-primary wide-button pull-left"
                    onClick={(e) => handleChange(e)}
                >
                    {t('Back to Guided Search')}
                </button>
                <button
                    className="btn btn-primary wide-button mrgn-lft-md"
                    onClick={(e) => window.location.reload()}
                >
                    {t('Start Over')}
                </button>
            </Box>

            <section className="most-requested well well-sm brdr-1" style={{ marginTop: '25px' }}>
                    <h2 className="mrgn-tp-md" style={{ fontSize: "32px", padding: '0 10px' }}>{t("Disclaimer")}</h2>
                    <div className="row profile">
                        <div className="col-md-12" style={{ padding: '0 25px' }}>
                        <p>
                            {t("DisclaimerPart1")}
                            <a href={`/${i18n.language}/faq`} target="_blank" rel="noopener noreferrer">{t("Frequently Asked Questions")}</a>
                            {t(".")}
                        </p>
                        <p>
                            <Trans i18nKey="DisclaimerPart2" components={{ sup: <sup /> }} />
                        </p>
                        <p>
                            {t("DisclaimerPart3")}
                        </p>
                        <p>
                            {t("DisclaimerPart4")}
                        </p>
                        </div>
                    </div>
            </section>
        </>
    );
}